import React, { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";

const FAQS = [
  {
    q: "Where is Rocket Card Shop located?",
    a: "We're in Springtown, TX at 219 Cactus Ridge Ct, 76082 — a short drive from Weatherford, Azle, and the greater Fort Worth / DFW area. Because we're a private appointment-only shop, the exact entry instructions are sent when your visit is confirmed.",
  },
  {
    q: "Why are you appointment-only?",
    a: "It lets us give every customer the table to themselves — no waiting, no crowds, no rushed conversations. You get our full attention, full access to the case, and a private experience whether you're spending $20 or $5,000.",
  },
  {
    q: "How do I book an appointment?",
    a: "Use the Contact form on this site, send us a message, and we'll reply with a confirmed time slot (usually within 24 hours). Tell us roughly what you're after — sealed product, slabs, singles, or a buy/sell/trade — so we can pull the right inventory before you arrive.",
  },
  {
    q: "What Pokémon products do you carry?",
    a: "Sealed (booster boxes, ETBs, collection boxes, blisters, tins, sleeved packs), graded slabs (PSA, BGS, CGC — vintage and modern), and raw singles across every era. Our sealed inventory is listed live on this page; slabs and singles rotate constantly and are kept in-shop only.",
  },
  {
    q: "Do you buy and trade cards?",
    a: "Yes — we buy outright and trade in both directions. Use the Buy / Sell / Trade form above to send a photo and quick description of what you have. We reply with a fair, market-based offer (cash, trade credit, or cards in return).",
  },
  {
    q: "Do you accept graded slabs (PSA, BGS, CGC)?",
    a: "Absolutely. Vintage holos, modern alt-art chase cards, one-of-ones — bring them in. We don't grade cards in-house, but we work with collectors who do and can recommend grading services if you're considering submitting.",
  },
  {
    q: "What forms of payment do you accept?",
    a: "Cash and major credit cards. For larger purchases, we can also arrange Zelle or a cashier's check — just let us know ahead of your appointment.",
  },
  {
    q: "Do you also carry One Piece or Dragon Ball Super cards?",
    a: "Pokémon is our specialty, but we do keep select sealed One Piece and Dragon Ball Super product in stock. Check the inventory list and filter by brand — or ask in your appointment request.",
  },
];

function FaqItem({ q, a, open, onToggle, idx }) {
  return (
    <div
      data-testid={`faq-item-${idx}`}
      className={`nb-card !shadow-none border-b-4 border-black ${
        open ? "bg-white" : "bg-white"
      }`}
      style={{ boxShadow: "none" }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={`faq-question-${idx}`}
        className="w-full flex items-start gap-4 px-5 md:px-7 py-5 text-left"
      >
        <span className="font-heading text-2xl text-[#FF1F1F] leading-none w-10 shrink-0 mt-1">
          {String(idx + 1).padStart(2, "0")}
        </span>
        <span className="flex-1 font-heading uppercase text-lg sm:text-xl leading-tight">
          {q}
        </span>
        <ChevronDown
          size={24}
          strokeWidth={3}
          className={`shrink-0 mt-1 transition-transform duration-200 ${
            open ? "rotate-180 text-[#FF1F1F]" : "text-black"
          }`}
        />
      </button>
      <div
        data-testid={`faq-answer-${idx}`}
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <p className="px-5 md:px-7 pb-6 pl-[3.75rem] md:pl-[4.5rem] text-base text-black/80 leading-relaxed">
            {a}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function Faq() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="bg-white border-b-4 border-black"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <span className="tag-chip" data-testid="faq-eyebrow">
              /// FAQ
            </span>
            <h2
              data-testid="faq-heading"
              className="mt-6 font-heading uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9]"
            >
              Quick <br />
              <span className="text-[#FF1F1F]">answers.</span>
            </h2>
            <p className="mt-6 text-base text-black/75 leading-relaxed max-w-md">
              Common questions about booking visits, what we carry, and how
              buying, selling, and trading work at Rocket Card Shop.
            </p>
            <div
              className="mt-8 p-5 inline-flex items-start gap-3 border-4 border-black"
              style={{
                background: "#FF1F1F",
                color: "#FFFFFF",
                boxShadow: "8px 8px 0px 0px rgba(10,10,10,1)",
              }}
              data-testid="faq-help-card"
            >
              <HelpCircle className="shrink-0 mt-0.5 text-white" size={22} strokeWidth={2.5} />
              <div className="text-white">
                <div className="font-mono text-xs uppercase tracking-widest font-bold text-white">
                  Still curious?
                </div>
                <div className="font-heading uppercase text-xl mt-1 text-white">
                  Send us a message — we&apos;ll reply within a day.
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="nb-card divide-y-0 overflow-hidden bg-white" data-testid="faq-list">
              {FAQS.map((item, idx) => (
                <FaqItem
                  key={item.q}
                  q={item.q}
                  a={item.a}
                  idx={idx}
                  open={openIdx === idx}
                  onToggle={() => setOpenIdx(openIdx === idx ? -1 : idx)}
                />
              ))}
            </div>

            {/* FAQ JSON-LD for Google rich results */}
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: FAQS.map(({ q, a }) => ({
                    "@type": "Question",
                    name: q,
                    acceptedAnswer: { "@type": "Answer", text: a },
                  })),
                }),
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
